// Component: ConditionRailTree.jsx
// Purpose: ONE shared condition tree under an Entry/Exit trigger row. Every AND/OR/NOT gate on the path is
//   its own row (pill + own value chip), its inputs are child rows joined by measured SVG elbow connectors,
//   any gate subtree and the whole trigger can be collapsed, and each row states whether it currently
//   satisfies its gate. Used by the dashboard PositionGroup and the Monitoring SignalEvaluationPanel, and (with
//   the default-off `authorMode` props) by the Strategy Builder Tree/Outline through the same row pipeline.
//   Display only: the component holds no operator, polarity or tint logic, it renders the rows that
//   nodeUtils.annotateTriggerTree derives (and nodeUtils.buildConditionDetail's text, passed in as strings).

import React from 'react';
import { FiChevronDown, FiChevronRight } from 'react-icons/fi';
import { annotateTriggerTree } from '../utils/nodeUtils';
import { getNodeAccent } from '../config/nodeVisualConfig';

/*>>> CONSTANTS <<<*/
//--> Gates at depth >= this start collapsed (trigger = depth 0).
const AUTO_COLLAPSE_DEPTH = 4;
const INDENT_PX = 14;
const PILL_TEXT = { AND: 'ALL', OR: 'ANY', NOT: 'NOT' };
//* Hint chip: 9-10px type scale, theme tokens only.
const WARN_STYLE = { color: 'color-mix(in srgb, var(--warning) 55%, var(--fg-1))' };
//--> author rows (Strategy Builder): 26 px indent; the connector spine runs under the parent chevron midline and
//    the tick stops 2 px short of the child row (dot centre -> chevron midline = 8 + 7 gap + 4 = 19 px).
const AUTHOR_INDENT_PX = 26;
const AUTHOR_SPINE_DX = 19;
const AUTHOR_SPINE_DY = 9;
const AUTHOR_TICK_DX = 35;
const AUTHOR_STROKE = { stroke: 'var(--divider)', strokeWidth: 1 };
const SIDE_LABEL = { left: 'Left', right: 'Right', from: 'From', to: 'To' };
export const ROW_ACTION_CLASS = 'inline-flex h-5 w-5 items-center justify-center rounded text-fg-3 hover:bg-bg-2 hover:text-fg-1 focus-visible:ring-1 focus-visible:ring-primary';
const STROKE = {
  satisfied: 'var(--color-profit)',
  unsatisfied: 'var(--color-loss)',
  invalid: 'var(--warning)',
  neutral: 'var(--fg-3)',
};

/*>>> PURE LAYOUT <<<*/
/**
 * connectorPaths — one elbow per rendered parent -> child edge (visible rows only), 'M px py V cy H cx'.
 * `rects` is { [rowKey]: { x, y } }, the dot centres relative to the rows container; a missing rect reads
 * { x: 0, y: 0 }. The style is taken from the CHILD's gate state (colour changes only at a junction).
 * `author` (Strategy Builder) draws the neutral divider-coloured spine/tick under the chevrons, with no lit state.
 */
export function connectorPaths(rows, rects, author = false) {
  const visible = new Set((rows || []).map((r) => r.key));
  const at = (k) => (rects && rects[k]) || { x: 0, y: 0 };
  return (rows || [])
    .filter((r) => r.kind !== 'trigger' && r.parentKey != null && visible.has(r.parentKey))
    .map((r) => {
      const p = at(r.parentKey);
      const c = at(r.key);
      if (author) {
        const d = `M ${p.x - AUTHOR_SPINE_DX} ${p.y + AUTHOR_SPINE_DY} V ${c.y} H ${c.x - AUTHOR_TICK_DX}`;
        return { key: r.key, d, style: AUTHOR_STROKE, kind: 'neutral', blocking: false };
      }
      const kind = r.invalidReason ? 'invalid' : r.state;
      const style = {
        stroke: STROKE[kind] || STROKE.neutral,
        strokeDasharray: kind === 'unsatisfied' ? '4 3' : kind === 'invalid' ? '1.5 3' : undefined,
        strokeWidth: kind === 'satisfied' ? 1.5 : 1,
      };
      return { key: r.key, d: `M ${p.x} ${p.y} V ${c.y} H ${c.x}`, style, kind, blocking: r.state === 'unsatisfied' };
    });
}

/*>>> ROW HELPERS <<<*/
const chipClass = (state, invalid) => {
  if (invalid) return '';
  if (state === 'satisfied') return 'text-[var(--color-profit)]';
  if (state === 'unsatisfied') return 'text-[var(--color-loss)]';
  return 'text-fg-2';
};
const tintClass = (state) => {
  if (state === 'satisfied') return 'bg-[color-mix(in_oklch,var(--color-profit)_8%,transparent)]';
  if (state === 'unsatisfied') return 'bg-[color-mix(in_oklch,var(--color-loss)_8%,transparent)]';
  return '';
};
const gateSummary = (row) => {
  if (!row.summary) return null;
  if (row.summary.op == null) return null; // unknown operator: no any-of / all-of claim
  if (row.summary.op === 'NOT') return 'inverts its input';
  const word = row.summary.op === 'AND' ? 'all' : 'any';
  return `${word} of ${row.summary.total} · ${row.summary.met} met`;
};

function Chip({ text, state, invalid }) {
  return (
    <span
      className={`flex-shrink-0 whitespace-nowrap rounded-full px-1.5 py-px text-[9px] font-bold uppercase tracking-tight ring-1 ring-inset ring-divider ${chipClass(state, invalid)}`}
      style={invalid ? WARN_STYLE : undefined}
    >
      {text}
    </span>
  );
}

function Chevron({ collapsed, hidden, onToggle, toggleKey }) {
  //--> author mode (toggleKey set): keys are not swallowed, so arrow keys reach the tree's roving handler
  const author = toggleKey != null;
  return (
    <>
      <button
        type="button"
        aria-expanded={!collapsed}
        aria-label={collapsed ? 'Expand' : 'Collapse'}
        className={author ? `${ROW_ACTION_CLASS} mt-0.5 flex-none` : 'flex-shrink-0 px-0.5 text-[9px] leading-none text-fg-2'}
        data-tree-toggle={author ? toggleKey : undefined}
        onClick={(e) => { e.stopPropagation(); onToggle(); }}
        onKeyDown={author ? undefined : (e) => e.stopPropagation()}
      >
        {author
          ? (collapsed ? <FiChevronRight size={16} aria-hidden="true" /> : <FiChevronDown size={16} aria-hidden="true" />)
          : <span aria-hidden="true">{collapsed ? '▸' : '▾'}</span>}
      </button>
      {collapsed && hidden != null && <span className="flex-shrink-0 text-[9px] font-semibold tabular-nums text-fg-2">+{hidden}</span>}
    </>
  );
}

/*>>> AUTHOR-MODE SLOTS (operand chip + drag metadata; rows themselves use the one rowShell below) <<<*/
//* the model owns the drag source (incl. the exact sourceHandle, null/undefined kept as-is); the renderer only
//  serialises it (B2). A child occurrence carries the edge it would move (its first parent edge).
const authorDragValue = (row) => {
  if (row?.connectable === false || !row?.dragSource) return null;
  return { source: row.dragSource.nodeId, sourceHandle: row.dragSource.sourceHandle, removeEdgeIds: row.incomingEdgeIds || [] };
};

const sameNodeAttr = (nodeId, sameNodeId) => (nodeId && nodeId === sameNodeId ? 'true' : 'false');
const selectedNodeAttr = (nodeId, selectedId) => (nodeId && selectedId != null && nodeId === selectedId ? 'true' : undefined);

//--> one operand on the row-line sentence: a coloured chip (node accent via --c), a plain constant, or the warning
//    'pick input' slot. Every one is a [data-tree-chip]; an input slot's chip is also its drop target.
function OperandChip({
  chip, slot, sameNodeId, selectedId, rowNodeId, allowDrop = true,
  onSelectNode, onPickOperand, onAddCondition,
}) {
  const dragValue = chip?.connectable === false ? null : chip?.dragSource;
  const dropTarget = allowDrop ? slot?.dropTarget : null;
  const side = slot?.side || chip?.kind || 'input';
  const slotKey = `${rowNodeId}:${slot?.handleId ?? side}`;
  const isConstant = chip?.kind === 'constant';
  const missing = !chip || (!chip.nodeId && !isConstant);
  //--> readiness flags of the operand node itself (B3) ring the chip; their text is in the detail block. A chip of
  //    the row's own node (time_window bounds) is already flagged on the row, so it is not repeated.
  const flags = chip?.nodeId && chip.nodeId !== rowNodeId ? (chip.readinessLabels || []) : [];
  const commonAttrs = {
    'data-tree-chip': 'true',
    'data-operand-slot-key': slotKey,
    'data-tree-drag-source': dragValue ? JSON.stringify({ source: dragValue.nodeId, sourceHandle: dragValue.sourceHandle }) : undefined,
    'data-tree-drop-target': dropTarget ? JSON.stringify(dropTarget) : undefined,
    'data-tree-locked': chip?.connectable === false ? 'true' : undefined,
    'data-readiness': flags.length > 0 ? 'true' : undefined,
    draggable: dragValue ? true : undefined,
  };
  //* while a drag is in flight (data-dragging on the tree root) every drop-target chip shows its full ring
  const dropRing = dropTarget ? ' [[data-dragging=true]_&]:shadow-[inset_0_0_0_1.5px_var(--c)]' : '';
  const pickerIntent = {
    ...(dropTarget || { target: rowNodeId, targetHandle: slot?.handleId }),
    slotKey,
  };
  const canPickOperand = allowDrop && slot?.inputKind !== 'boolean' && !!dropTarget;
  if (missing) {
    const booleanInput = slot?.inputKind === 'boolean';
    const emptyLabel = booleanInput ? 'Add condition…' : 'pick input';
    if (!allowDrop) {
      return (
        <span
          {...commonAttrs}
          aria-label={`${side} ${booleanInput ? 'condition' : 'operand'} empty`}
          data-same-node={sameNodeAttr(null, sameNodeId)}
          className="inline-flex items-center whitespace-nowrap rounded-[5px] bg-warning/10 px-1.5 py-px font-sans text-[12px] font-medium text-fg-3"
        >
          not connected
        </span>
      );
    }
    return (
      <button
        type="button"
        {...commonAttrs}
        aria-label={`${side} ${booleanInput ? 'condition' : 'operand'} empty`}
        data-same-node={sameNodeAttr(null, sameNodeId)}
        onClick={(event) => {
          const activate = booleanInput ? onAddCondition : onPickOperand;
          if (!activate) return;
          event.stopPropagation();
          if (booleanInput) activate(event, { nodeId: pickerIntent.target, targetHandle: pickerIntent.targetHandle });
          else activate(pickerIntent);
        }}
        className={`inline-flex items-center whitespace-nowrap rounded-[5px] bg-warning/15 px-1.5 py-px font-sans text-[12px] font-medium text-warning [--c:var(--warning)]${dropRing}`}
      >
        {emptyLabel}
      </button>
    );
  }
  if (isConstant && !chip.nodeId) {
    return (
      <span className="inline-flex items-stretch">
        <span
          {...commonAttrs}
          aria-label={`${side} operand ${chip.label}`}
          data-same-node={sameNodeAttr(chip.nodeId, sameNodeId)}
          data-selected-node={selectedNodeAttr(chip.nodeId, selectedId)}
          className={`rounded-[5px] px-[3px] font-mono text-[12px] text-fg-1${dropRing}`}
        >
          {chip.name ?? chip.label}
        </span>
        {canPickOperand ? (
          <button
            type="button"
            aria-label={`Change ${side} operand`}
            data-operand-slot-key={slotKey}
            data-same-node={sameNodeAttr(chip.nodeId, sameNodeId)}
            data-selected-node={selectedNodeAttr(chip.nodeId, selectedId)}
            onClick={(event) => {
              event.preventDefault();
              event.stopPropagation();
              onPickOperand?.(pickerIntent);
            }}
            className="ml-0.5 rounded-r-[5px] px-1 text-[10px] text-fg-3 hover:bg-bg-3 hover:text-fg-1"
          >
            Change
          </button>
        ) : null}
      </span>
    );
  }
  const uses = chip.usageCount > 1 && chip.kind !== 'price' ? chip.usageCount : null;
  const segments = chip.segments?.length > 0 ? chip.segments : [{
    nodeId: chip.nodeId,
    type: chip.node?.type,
    data: chip.node?.data || {},
    label: chip.name ?? chip.label,
    accent: getNodeAccent(chip.node?.type, chip.node?.data || {}),
    ancestorTrail: chip.ancestorTrail || slot?.ancestorTrail || [],
  }];
  return (
    <span data-operand-segments="true" className="inline-flex items-stretch">
      {segments.map((segment, segmentIndex) => {
        const secondaryText = segment.authoredName || segment.sourceTitle;
        const selectSegment = (event) => {
          if (!onSelectNode) return;
          event.preventDefault();
          event.stopPropagation();
          onSelectNode(segment.nodeId, segment.ancestorTrail || []);
        };
        return (
          <button
            type="button"
            key={`${segment.nodeId}:${segment.field || ''}:${segmentIndex}`}
            {...commonAttrs}
            aria-label={`${side} operand ${segment.label}${secondaryText ? ` ${secondaryText}` : ''}`}
            data-node-id={segment.nodeId || undefined}
            data-operand-slot-key={slotKey}
            data-same-node={sameNodeAttr(segment.nodeId, sameNodeId)}
            data-selected-node={selectedNodeAttr(segment.nodeId, selectedId)}
            onClick={selectSegment}
            onKeyDown={(event) => { if (event.key === 'Enter') selectSegment(event); }}
            title={[segment.authoredName, segment.detail, segment.type || chip.kind, chip.usageCount > 1 ? `read by ${chip.usageCount} nodes` : null, ...flags].filter(Boolean).join(' · ')}
            className={`inline-flex items-center gap-[3px] whitespace-nowrap border-r border-[color-mix(in_srgb,var(--c)_25%,transparent)] bg-[color-mix(in_srgb,var(--c)_13%,transparent)] px-1.5 py-px font-mono text-[12px] font-medium text-fg-1 first:rounded-l-[5px] last:rounded-r-[5px] last:border-r-0 shadow-[inset_0_0_0_1px_color-mix(in_srgb,var(--c)_35%,transparent)] hover:shadow-[inset_0_0_0_1.5px_var(--c)] data-[same-node=true]:shadow-[inset_0_0_0_1.5px_var(--c)] motion-safe:transition-[background-color,box-shadow,opacity] motion-safe:duration-[120ms]${dropRing}`}
            style={{ '--c': segment.accent, boxShadow: flags.length > 0 ? 'inset 0 0 0 1.5px var(--warning)' : undefined }}
          >
            {segment.label}
            {secondaryText ? (
              <span className="text-[10px] text-fg-3">
                {secondaryText}{segment.sourceTitle && segment.data?.timeframe ? ` · ${segment.data.timeframe}` : ''}
              </span>
            ) : null}
            {segmentIndex === segments.length - 1 && chip.barsAgoTag ? <span className="text-[10px] text-fg-3">· {chip.barsAgoTag}</span> : null}
            {segmentIndex === segments.length - 1 && uses ? <sup className="font-sans text-[9.5px] font-semibold text-fg-3">×{uses}</sup> : null}
          </button>
        );
      })}
      {canPickOperand ? (
        <button
          type="button"
          aria-label={`Change ${side} operand`}
          data-node-id={chip.nodeId || undefined}
          data-operand-slot-key={slotKey}
          data-same-node={sameNodeAttr(chip.nodeId, sameNodeId)}
          data-selected-node={selectedNodeAttr(chip.nodeId, selectedId)}
          onClick={(event) => {
            event.preventDefault();
            event.stopPropagation();
            onPickOperand?.(pickerIntent);
          }}
          className="ml-0.5 rounded-r-[5px] px-1 text-[10px] text-fg-3 hover:bg-bg-3 hover:text-fg-1"
        >
          Change
        </button>
      ) : null}
    </span>
  );
}

/*>>> NAME DETAIL (50-21 R10 / R13) <<<*/
//* Shown inside a row's existing detail block, never a second component per surface: the CURRENT
//  auto-assembled logic text (only passed while the record holds a custom name) and the author's
//  notes (only when non-empty after trim). Both are plain React text nodes — notes keep their line
//  breaks via `pre-wrap` and are NEVER parsed as markup (R13-T3).
function NameDetail({ autoText, notes }) {
  const hasNotes = typeof notes === 'string' && notes.trim() !== '';
  if (!autoText && !hasNotes) return null;
  return (
    <>
      {autoText ? <div data-testid="row-auto-text" className="col-span-full w-full min-w-0 break-words">{autoText}</div> : null}
      {hasNotes ? (
        <div data-testid="row-notes" className="col-span-full w-full min-w-0 break-words font-sans" style={{ whiteSpace: 'pre-wrap' }}>{notes}</div>
      ) : null}
    </>
  );
}

//--> one detail line per slot: kind · params · full lookback · fan-out, then the operand's readiness labels
function SlotDetail({ slot, rowNodeId }) {
  const chip = slot.chip;
  const missing = !chip || (!chip.nodeId && chip.kind !== 'constant');
  const text = missing ? 'not connected' : [
    chip.kind,
    chip.paramsText,
    chip.barsAgoTag,
    chip.usageCount > 1 ? `read by ${chip.usageCount} nodes` : null,
  ].filter(Boolean).join(' · ');
  const flags = chip?.nodeId && chip.nodeId !== rowNodeId ? (chip.readinessLabels || []) : [];
  return (
    <>
      <span className="text-[10px] uppercase tracking-wide text-fg-3">{SIDE_LABEL[slot.side] || slot.side}</span>
      <span className="text-fg-2">
        {text}
        {flags.length > 0 ? <span data-testid="operand-chip-flag" className="text-warning"> · {flags.join(', ')}</span> : null}
      </span>
    </>
  );
}

//--> author rows arrive nested from builderTreeModel; flatten to the same { key, depth, parentKey } row list the
//    live path gets from annotateTriggerTree, skipping descendants of collapsed rows. Inputs are never mutated.
function flattenAuthorRows(roots, collapsedKeys) {
  const out = [];
  const visit = (row, depth, parentKey) => {
    const d = row.level ? row.level - 1 : depth;
    out.push({ ...row, id: row.nodeId, depth: d, parentKey, authorRow: row });
    if (collapsedKeys.has(row.key)) return;
    (row.children || []).forEach((child) => visit(child, d + 1, row.key));
  };
  (roots || []).forEach((row) => visit(row, 0, null));
  return out;
}

const EMPTY_SET = new Set();

/*>>> COMPONENT <<<*/
/**
 * Props
 *  tree        collectTriggerTree result ({ node, children }); an empty-children copy for a self-contained trigger.
 *  triggerRow  { name, detail, conditionText?, conditionDetail?, title, state, statusLabel, aside? }
 *  rowOf(id)   -> { name, detail?, conditionText?, operatorText?, value: true|false|null, invalidReason?, chipText, railState }
 *  accent      { dot, triggerReady } class tokens for the trigger dot / ready chip
 *  flow        'accent' | 'warning' | null  (spine lights only when the signal actually flows to the trigger)
 *  showCount   show 'n / N met' on the trigger row (default true)
 *  onRowClick(node), selectedId
 *
 * Author mode (Strategy Builder; every prop below defaults OFF, so live surfaces render exactly as before)
 *  authorMode        true -> rows come from `authorRows`, no met/unmet state, ARIA treeitems, wrapping text
 *  authorRows        builderTreeModel section roots ({ key, nodeId, kind, label, level, children, inputSlots })
 *  collapsedKeys     controlled collapse set (container-owned); onToggleRow(key) flips one row
 *  openDetailKey     controlled key of the ONE row whose detail block (Rule line + one line per slot) is open
 *  activeKey         roving-tabindex row key; sameNodeId marks every occurrence of the hovered/selected node
 *  renderRowAction   (row, { hasDetail, detailOpen, detailId, isTreeRewireTarget }) -> row-action slot, rendered
 *                    at the end of the row line (hover/focus/selection, and always while the row's detail is open)
 */
export default function ConditionRailTree({
  tree,
  triggerRow,
  rowOf,
  accent = {},
  flow = null,
  showCount = true,
  onRowClick,
  selectedId,
  authorMode = false,
  authorRows,
  collapsedKeys = EMPTY_SET,
  openDetailKey = null,
  onToggleRow,
  activeKey,
  sameNodeId,
  renderRowAction,
  labelMode = 'names',
  onSelectNode,
  onPickOperand,
  onAddCondition,
}) {
  //--> id prefix for author detail blocks (aria-controls target); renders nothing, so live markup is unchanged
  const detailIdPrefix = React.useId();
  /*>>> DERIVED ROWS <<<*/
  //--> ONE row pipeline: live rows from annotateTriggerTree, author rows flattened from the builder model.
  const rows = React.useMemo(
    () => (authorMode ? [] : annotateTriggerTree(tree, rowOf)),
    [authorMode, tree, rowOf],
  );
  const authorVisible = React.useMemo(
    () => (authorMode ? flattenAuthorRows(authorRows, collapsedKeys) : []),
    [authorMode, authorRows, collapsedKeys],
  );
  const trigger = rows[0];
  const hasChildren = rows.length > 1;

  /*>>> LOCAL UI STATE <<<*/
  //--> `collapsed` holds row keys (the trigger's own key included). `expandedKey` is ONE row key, so a node
  //    shown at two positions expands only where it was clicked.
  const initialCollapsed = React.useCallback(
    (list) => new Set(list.filter((r) => r.kind === 'gate' && r.depth >= AUTO_COLLAPSE_DEPTH).map((r) => r.key)),
    [],
  );
  const [collapsed, setCollapsed] = React.useState(() => initialCollapsed(rows));
  const [expandedKey, setExpandedKey] = React.useState(null);
  const firstTree = React.useRef(true);
  React.useEffect(() => {
    if (firstTree.current) { firstTree.current = false; return; }
    setCollapsed(initialCollapsed(rows));
    setExpandedKey(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- reset only when the graph (tree) changes, not on value ticks
  }, [tree]);

  const toggle = (key) => setCollapsed((prev) => {
    const next = new Set(prev);
    if (next.has(key)) next.delete(key); else next.add(key);
    return next;
  });

  //--> Visible rows: everything below a collapsed row is hidden.
  //    (author rows arrive already collapse-filtered; `rows` is empty in author mode)
  const visible = authorMode ? authorVisible : [];
  let hideBelow = null;
  if (!authorMode) rows.forEach((r) => {
    if (hideBelow != null && r.depth > hideBelow) return;
    hideBelow = null;
    visible.push(r);
    if (collapsed.has(r.key)) hideBelow = r.depth;
  });

  /*>>> CONNECTOR MEASUREMENT <<<*/
  //--> Only the dot centres are measured (not on value ticks); the path COUNT comes from the rows.
  const containerRef = React.useRef(null);
  const dotRefs = React.useRef({});
  const [rects, setRects] = React.useState({});
  const measure = React.useCallback(() => {
    const box = containerRef.current?.getBoundingClientRect?.();
    if (!box) return;
    const next = {};
    Object.entries(dotRefs.current).forEach(([key, el]) => {
      if (!el) return;
      const r = el.getBoundingClientRect();
      next[key] = { x: r.left - box.left + r.width / 2, y: r.top - box.top + r.height / 2 };
    });
    setRects(next);
  }, []);
  React.useLayoutEffect(() => {
    measure();
    if (typeof ResizeObserver === 'undefined' || !containerRef.current) return undefined;
    const ro = new ResizeObserver(() => measure());
    ro.observe(containerRef.current);
    return () => ro.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- layout depends on structure, collapse and expansion only
  }, [tree, collapsed, expandedKey, authorVisible]);

  const paths = connectorPaths(visible, rects, authorMode);

  /*>>> RENDER <<<*/
  const triggerCollapsed = authorMode ? false : collapsed.has(trigger.key);
  const triggerState = triggerRow?.state;
  const blockers = (trigger && trigger.blockers) || [];
  const showWhy = triggerState === 'waiting' && blockers.length > 0 && hasChildren && triggerCollapsed;
  const whyText = `Blocked by ${blockers
    .map((b) => b.view.name)
    .join(' · ')}`;

  //--> `author` (author mode only) carries the ARIA treeitem + drag/drop attributes; live rows pass none of it,
  //    so every author attribute is undefined there and the live markup is unchanged.
  const rowShell = (r, { dotNode, lead, content, detail, expanded, clickable, onClick, title, railState, gateState, selected, warn, author, trailing }) => (
    <div
      key={r.key}
      data-testid="rail-row"
      data-node-id={r.id}
      data-depth={r.depth}
      data-rail-state={railState}
      data-gate-state={gateState}
      className={author ? 'group/row relative z-10 rounded text-fg-1' : 'relative z-10'}
      style={{ paddingLeft: r.depth * (author ? AUTHOR_INDENT_PX : INDENT_PX) }}
      {...(author ? author.attrs : null)}
    >
      <div
        className={author ? author.lineClassName : `flex items-center gap-1.5 min-w-0 rounded px-1.5 py-[3px] ${tintClass(gateState)} ${warn ? 'border border-warning/40' : ''} ${selected ? 'ring-1 ring-[var(--node-visual)]' : ''}`}
      >
        {author ? lead : dotNode}
        {author ? dotNode : lead}
        {/* the click target is a SIBLING of the chevron button: interactive controls are never nested */}
        <div
          //--> author rows: `contents`, so the sentence parts are flex items of the line itself (the sentence wraps
          //    inside its own box in a narrow panel, e.g. the 360 px Outline)
          data-testid={author ? 'author-row-content' : undefined}
          className={author ? 'contents' : `flex flex-1 items-center min-w-0 gap-1.5 ${clickable ? 'cursor-pointer' : ''}`}
          role={clickable ? 'button' : undefined}
          tabIndex={clickable ? 0 : undefined}
          aria-expanded={expanded}
          title={title || undefined}
          onClick={clickable ? onClick : undefined}
          onKeyDown={clickable ? (e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onClick(); } } : undefined}
        >
          {content}
        </div>
        {trailing}
      </div>
      {detail ? (
        <div className={author?.detailClassName || 'pl-5 text-[10px] font-mono tabular-nums text-fg-2 break-words'} {...(author ? author.detailAttrs : null)}>
          {detail}
        </div>
      ) : null}
    </div>
  );

  const dot = (r, { hollow, cross, filled, warn, accent: authorAccent, ring }) => (authorAccent ? (
    <span
      ref={(el) => { dotRefs.current[r.key] = el; }}
      data-testid="rail-spine"
      data-ring={ring ? 'true' : undefined}
      aria-hidden="true"
      className={`relative z-10 mt-[7px] h-2 w-2 flex-none rounded-full ${ring ? 'bg-transparent shadow-[inset_0_0_0_1.5px_var(--c)]' : 'bg-[var(--c)]'}`}
      style={{ '--c': authorAccent }}
    />
  ) : (
    <span
      ref={(el) => { dotRefs.current[r.key] = el; }}
      data-testid="rail-spine"
      data-lit={flow || 'none'}
      aria-hidden="true"
      className={`relative z-10 flex-shrink-0 inline-flex items-center justify-center rounded-full border ${r.kind === 'trigger' ? 'h-[9px] w-[9px] border-2' : 'h-[7px] w-[7px]'} ${
        warn ? 'border-warning bg-bg-2'
          : filled ? 'border-transparent bg-[var(--color-profit)]'
            : cross ? 'border-[var(--color-loss)] bg-bg-2'
              : hollow ? 'border-fg-3 bg-bg-2' : 'border-fg-3 bg-bg-3'
      }`}
    >
      {cross ? <span className="text-[7px] leading-none text-[var(--color-loss)]">×</span> : null}
    </span>
  ));

  const renderTrigger = () => {
    const r = trigger;
    const hasCondDetail = !!triggerRow?.conditionDetail;
    const isOpen = expandedKey === r.key;
    const clickable = hasCondDetail || !!onRowClick;
    const lit = flow === 'accent' || flow === 'warning';
    const chipCls =
      triggerState === 'deferred' ? ''
        : triggerState === 'fired' || triggerState === 'ready' ? (accent.triggerReady || 'text-fg-1')
          : 'text-fg-2 ring-1 ring-inset ring-divider';
    return (
      <React.Fragment key={r.key}>
        {rowShell(r, {
          dotNode: (
            <span
              ref={(el) => { dotRefs.current[r.key] = el; }}
              data-testid="rail-spine"
              data-lit={flow || 'none'}
              aria-hidden="true"
              className={`relative z-10 flex-shrink-0 rounded-full border-2 h-[9px] w-[9px] ${
                flow === 'warning' ? 'border-warning bg-warning/70'
                  : lit ? `border-transparent ${accent.dot || 'bg-fg-1'}` : 'border-fg-3 bg-bg-2'
              }`}
            />
          ),
          lead: hasChildren ? <Chevron collapsed={triggerCollapsed} hidden={r.descendantCount} onToggle={() => toggle(r.key)} /> : null,
          content: (
            <>
              <span className="min-w-0 truncate text-[11px] font-semibold text-fg-1" title={triggerRow?.name}>{triggerRow?.name}</span>
              {triggerRow?.conditionText ? (
                <span className="min-w-0 truncate text-[10px] font-mono tabular-nums text-fg-2">{triggerRow.conditionText}</span>
              ) : null}
              <span className="flex-1" />
              {showCount && trigger.count && trigger.count.total > 0 && (
                <span
                  className="flex-shrink-0 text-[9px] font-semibold tabular-nums text-fg-2"
                  aria-label={`${trigger.count.met} of ${trigger.count.total} met`}
                >
                  {trigger.count.met} / {trigger.count.total} met
                </span>
              )}
              <span
                className={`flex-shrink-0 whitespace-nowrap rounded-full px-1.5 py-px text-[9px] font-bold uppercase tracking-tight ${triggerState === 'deferred' ? '' : chipCls}`}
                style={triggerState === 'deferred' ? WARN_STYLE : undefined}
              >
                {triggerRow?.statusLabel}
              </span>
            </>
          ),
          detail: [triggerRow?.detail, isOpen ? triggerRow?.conditionDetail : null].filter(Boolean).join(' · ') || undefined,
          expanded: hasCondDetail ? isOpen : undefined,
          clickable,
          onClick: () => {
            if (hasCondDetail) setExpandedKey((prev) => (prev === r.key ? null : r.key));
            if (onRowClick) onRowClick(r.node);
          },
          title: triggerRow?.title,
          railState: triggerState,
          gateState: 'neutral',
          selected: selectedId != null && selectedId === r.id,
          warn: triggerState === 'deferred',
        })}
        {triggerRow?.aside ? <div data-testid="trigger-aside">{triggerRow.aside}</div> : null}
        {showWhy && (
          <div data-testid="why-not-fired" role="status" className="pl-5 py-0.5 text-[10px] font-medium" style={WARN_STYLE}>
            {whyText}
          </div>
        )}
      </React.Fragment>
    );
  };

  const renderRow = (r) => {
    const v = r.view || {};
    const invalid = !!r.invalidReason;
    const hasNameDetail = !!v.autoText || (typeof v.notes === 'string' && v.notes.trim() !== '');
    const canDetail = !!v.detail || hasNameDetail;
    const isOpen = expandedKey === r.key;
    const isGate = r.kind === 'gate';
    const textBit = v.conditionText || v.operatorText;
    //--> value-vs-colour word: derived only from the row's existing state + raw value; never beside another hint
    const valueWord = r.hint || invalid ? null
      : r.state === 'unsatisfied' && v.value === true ? 'blocks'
        : r.state === 'satisfied' && v.value === false ? 'as needed' : null;
    const hint = r.hint || valueWord;
    return rowShell(r, {
      dotNode: dot(r, {
        hollow: isGate,
        filled: !isGate && r.state === 'satisfied',
        cross: !isGate && r.state === 'unsatisfied',
        warn: invalid,
      }),
      lead: isGate && r.descendantCount > 0
        ? <Chevron collapsed={collapsed.has(r.key)} hidden={r.descendantCount} onToggle={() => toggle(r.key)} />
        : null,
      content: (
        <>
          {isGate && r.gateOp && (
            <span
              data-gate-op={r.gateOp}
              className="flex-shrink-0 rounded px-1 py-px text-[9px] font-bold tracking-tight text-fg-1 ring-1 ring-inset ring-divider"
            >
              {PILL_TEXT[r.gateOp]}
            </span>
          )}
          <span className={`min-w-0 truncate text-[11px] ${v.railState === 'wait' ? 'text-fg-2' : 'text-fg-1'} font-medium`} title={v.autoText || v.name}>{v.name}</span>
          {isGate && gateSummary(r) ? <span className="flex-shrink-0 text-[9px] text-fg-2">{gateSummary(r)}</span> : null}
          {!isGate && textBit ? <span className="min-w-0 truncate text-[10px] font-mono tabular-nums text-fg-2">{textBit}</span> : null}
          {hint ? <span className="flex-shrink-0 text-[9px] text-fg-2">{hint}</span> : null}
          <span className="flex-1" />
          <Chip text={v.chipText} state={r.state} invalid={invalid} />
        </>
      ),
      detail: canDetail && isOpen ? (
        <>
          {v.detail}
          <NameDetail autoText={v.autoText} notes={v.notes} />
        </>
      ) : undefined,
      expanded: canDetail ? isOpen : undefined,
      clickable: canDetail || !!onRowClick,
      onClick: () => {
        if (canDetail) setExpandedKey((prev) => (prev === r.key ? null : r.key));
        if (onRowClick) onRowClick(r.node);
      },
      title: v.title,
      railState: v.railState,
      gateState: r.state,
      selected: selectedId != null && selectedId === r.id,
      warn: invalid,
    });
  };

  //--> Author row: the SAME rowShell/dot/Chevron as a live row, with no met/unmet state. The row line IS the label:
  //    chevron · accent dot · one sentence (chips + operator, pill + name + summary, or a name), then the reference
  //    and readiness text and the hover-revealed row actions (details toggle, Locate) in the rowShell `trailing`
  //    slot. The detail block under it (Rule line + one line per slot, no buttons) is display:none unless the row's
  //    key is openDetailKey (the container toggles it from the info button / `i`). The line's chips are the drop
  //    targets. Clicks/keys are handled by the container.
  const renderAuthorRow = (r) => {
    const row = r.authorRow;
    const node = row.node || {};
    const data = node.data || {};
    const childCount = (row.children || []).length;
    const isCollapsed = collapsedKeys.has(row.key);
    const dragValue = authorDragValue(row);
    const isGroup = node.type === 'logic_gate';
    const slots = row.inputSlots || [];
    const allowDrop = row.connectable !== false && !row.isReference;
    const canAddCondition = allowDrop && !!row.conditionAttachTarget?.targetHandle;
    const hasBooleanDropTarget = slots.some((slot) => slot.inputKind === 'boolean' && slot.dropTarget);
    const hasOperandDropTarget = slots.some((slot) => slot.inputKind === 'operand' && slot.dropTarget);
    const isTreeRewireTarget = allowDrop && (canAddCondition || hasBooleanDropTarget || hasOperandDropTarget);
    const detailOpen = openDetailKey != null && openDetailKey === row.key;
    const detailId = `${detailIdPrefix}detail-${row.key}`;
    const chipOf = (slot, index) => (
      <OperandChip
        key={`${slot.handleId || slot.chip?.nodeId || 'chip'}:${index}`}
        chip={slot.chip}
        slot={slot}
        sameNodeId={sameNodeId}
        selectedId={selectedId}
        rowNodeId={row.nodeId}
        allowDrop={allowDrop}
        onSelectNode={onSelectNode}
        onPickOperand={onPickOperand}
        onAddCondition={onAddCondition}
      />
    );
    const detailChipOf = (slot, index) => {
      const chip = slot?.chip;
      const missing = !chip || (!chip.nodeId && chip.kind !== 'constant');
      return (
        <span
          key={`detail:${slot?.handleId || index}`}
          data-tree-detail-chip="true"
          data-tree-chip="true"
          data-node-id={chip?.nodeId || undefined}
          data-same-node={sameNodeAttr(chip?.nodeId, sameNodeId)}
          className="inline-flex items-center whitespace-nowrap rounded-[5px] bg-bg-3 px-1.5 py-px font-mono text-[12px] text-fg-2"
        >
          {missing ? 'not connected' : (chip.name ?? chip.label)}
          {!missing && chip.barsAgoTag ? <span className="ml-1 text-[10px] text-fg-3">· {chip.barsAgoTag}</span> : null}
        </span>
      );
    };
    const namedDetailChipOf = (slot, index) => {
      const chip = slot?.chip;
      const missing = !chip || (!chip.nodeId && chip.kind !== 'constant');
      return missing ? detailChipOf(slot, index) : chipOf(slot, index);
    };
    const sentence = (children) => (
      <span data-testid="author-row-sentence" className="mt-0.5 flex min-w-0 flex-1 flex-wrap items-center gap-x-[5px] gap-y-1 text-fg-2">{children}</span>
    );
    const comparatorSentence = (renderChip) => sentence(
      <>
        {renderChip(slots[0], 0)}
        <span className="font-mono text-[12px] text-fg-2">{row.operatorText}</span>
        {slots.slice(1).map((slot, index) => renderChip(slot, index + 1))}
      </>,
    );
    const timeWindowSentence = () => sentence(
      <>
        Time between {detailChipOf(slots[0], 0)} and {detailChipOf(slots[1], 1)} IST
      </>,
    );
    const nameSpan = (text) => <span className="mt-0.5 font-semibold text-fg-1">{text}</span>;
    const sum = (text) => (text ? <span className="mt-[3px] whitespace-nowrap text-[11.5px] text-fg-3">{text}</span> : null);

    let line;
    let detailSentence = null;
    //--> 50-21 Live-Val: a named condition row shows the name; its sentence moves to the detail block
    const namedCondition = labelMode === 'names' && !!row.name
      && (node.type === 'comparator' || node.type === 'time_window');
    const fieldsNameDetail = labelMode === 'fields' && row.name
      && (node.type === 'comparator' || node.type === 'time_window') ? row.name : null;
    const missingSlots = slots.filter(({ chip }) => (
      !chip || (!chip.nodeId && chip.kind !== 'constant')
    ));
    const missingBooleanSlots = missingSlots.filter(({ inputKind }) => inputKind === 'boolean');
    if (isGroup) {
      line = (
        <>
          <span className="mt-0.5 flex-none rounded px-[5px] py-px text-[10px] font-bold tracking-[.03em] text-[var(--node-logic)] shadow-[inset_0_0_0_1px_color-mix(in_srgb,var(--node-logic)_45%,transparent)]">
            {PILL_TEXT[row.gateOp] || 'LOGIC'}
          </span>
          {row.name ? nameSpan(row.name) : null}
          {sum(row.summaryText)}
        </>
      );
    } else if (node.type === 'comparator' && slots.length > 0) {
      line = comparatorSentence(chipOf);
    } else if (node.type === 'math' && slots.length > 0) {
      line = sentence(
        <>
          <span className="font-mono text-[11px] text-fg-3">{row.label}</span>
          {slots.map((slot, index) => chipOf(slot, index))}
        </>,
      );
    } else if (node.type === 'time_window') {
      line = timeWindowSentence();
    } else if (row.selfChip) {
      line = sentence(chipOf({ chip: row.selfChip }, 0));
    } else {
      line = (
        <>
          {nameSpan(row.label)}
          {row.kind === 'trigger' && data.qty != null && data.qty !== '' ? sum(`qty ${data.qty}`) : null}
        </>
      );
    }

    if (missingBooleanSlots.length > 0) {
      line = (
        <>
          {line}
          {missingBooleanSlots.map((slot, index) => chipOf(slot, index))}
        </>
      );
    }

    if (namedCondition) {
      detailSentence = node.type === 'comparator' ? comparatorSentence(namedDetailChipOf) : line;
      line = (
        <>
          {nameSpan(row.name)}
          {missingSlots.map((slot, index) => chipOf(slot, index))}
        </>
      );
    }

    //--> detail: the Rule line (the condition in plain words), one grid line per slot (Left/Right, From/To);
    //    a trigger also shows its action and quantity
    const triggerLines = row.kind === 'trigger'
      ? [['Action', data.action], ['Qty', data.qty]].filter(([, value]) => value != null && value !== '')
      : [];
    const hasNameDetail = !!row.autoText || (typeof row.notes === 'string' && row.notes.trim() !== '');
    const hasDetail = !!detailSentence || !!fieldsNameDetail || !!row.ruleText
      || slots.length > 0 || triggerLines.length > 0 || hasNameDetail;
    const action = renderRowAction
      ? renderRowAction(row, {
        hasDetail, detailOpen, detailId, isTreeRewireTarget, canAddCondition,
      })
      : null;
    const selectedHere = selectedId != null && row.nodeId === selectedId && row.key === activeKey;
    const sameHere = !selectedHere && row.nodeId != null && (row.nodeId === sameNodeId || row.nodeId === selectedId);
    return rowShell(r, {
      dotNode: dot(r, { accent: getNodeAccent(node.type, data), ring: row.unused || row.isReference }),
      lead: childCount > 0
        ? <Chevron collapsed={isCollapsed} toggleKey={row.key} onToggle={() => onToggleRow?.(row.key)} />
        : <span aria-hidden="true" className={`${ROW_ACTION_CLASS} invisible mt-0.5 flex-none`}><FiChevronRight size={16} /></span>,
      content: (
        <>
          {line}
          {row.referenceText ? <span className="mt-[3px] text-[11.5px] text-fg-3">{row.referenceText}</span> : null}
          {row.flagText ? (
            <span className="mt-[3px] flex-none rounded-full bg-warning/15 px-1.5 text-[10.5px] font-bold uppercase text-warning">{row.flagText}</span>
          ) : null}
        </>
      ),
      detail: hasDetail ? (
        <>
          {detailSentence ? <div data-testid="row-sentence-detail" className="col-span-full flex w-full min-w-0">{detailSentence}</div> : null}
          {fieldsNameDetail ? <div data-testid="row-authored-name-detail" className="col-span-full font-semibold text-fg-1">{fieldsNameDetail}</div> : null}
          {row.ruleText ? (
            <>
              <span className="text-[10px] uppercase tracking-wide text-fg-3">Rule</span>
              <span data-testid="author-row-rule" className="text-fg-1">{row.ruleText}</span>
            </>
          ) : null}
          <NameDetail autoText={row.autoText} notes={row.notes} />
          {slots.map((slot, index) => <SlotDetail key={`${slot.handleId || 'slot'}:${index}`} slot={slot} rowNodeId={row.nodeId} />)}
          {triggerLines.map(([label, value]) => (
            <React.Fragment key={label}>
              <span className="text-[10px] uppercase tracking-wide text-fg-3">{label}</span>
              <span className="text-fg-2">{String(value)}</span>
            </React.Fragment>
          ))}
        </>
      ) : undefined,
      //--> self-center: the info/Locate icons sit on the row's vertical centre, level with the chip line (Live-Val 10-27)
      trailing: action ? (
        <span className={`ml-auto inline-flex flex-none items-center gap-0.5 self-center transition-opacity ${detailOpen ? 'opacity-100' : 'opacity-0 group-hover/row:opacity-100 group-focus-within/row:opacity-100 group-aria-selected/row:opacity-100 [@media(hover:none)]:opacity-75'}`}>
          {action}
        </span>
      ) : null,
      clickable: false,
      selected: false,
      author: {
        lineClassName: `flex min-h-[30px] min-w-0 cursor-pointer items-start gap-[7px] rounded-md px-1.5 py-1 text-[13px] hover:bg-bg-3 motion-safe:transition-[background-color,box-shadow,opacity] motion-safe:duration-[120ms] ${
          selectedHere ? 'bg-primary/10 shadow-[inset_0_0_0_1px_color-mix(in_srgb,var(--primary)_40%,transparent)]'
            : sameHere ? 'shadow-[inset_0_0_0_1px_color-mix(in_srgb,var(--primary)_40%,transparent)]' : ''
        }`,
        //! a closed block must be display:none: the `hidden` attribute alone loses to the `grid` utility (same
        //  specificity, later in the cascade), so the closed block drops `grid` for the `hidden` utility as well
        detailClassName: detailOpen
          ? 'mb-1 ml-[46px] grid grid-cols-[auto_1fr] gap-x-3 gap-y-0.5 text-[11.5px]'
          : 'hidden',
        detailAttrs: {
          id: detailId,
          'data-testid': 'author-row-detail',
          'data-tree-detail': row.key,
          hidden: !detailOpen,
        },
        attrs: {
          role: 'treeitem',
          'aria-label': row.label,
          'aria-level': r.depth + 1,
          'aria-expanded': childCount > 0 ? !isCollapsed : undefined,
          'aria-selected': selectedId != null ? row.nodeId === selectedId : false,
          tabIndex: row.key === activeKey ? 0 : -1,
          'data-tree-row-key': row.key,
          'data-row-kind': row.kind,
          'data-detail-open': hasDetail ? String(detailOpen) : undefined,
          'data-tree-locked': row.connectable === false || row.deletable === false ? 'true' : undefined,
          'data-tree-drag-source': dragValue ? JSON.stringify(dragValue) : undefined,
          'data-tree-rewire-target': isTreeRewireTarget ? row.nodeId : undefined,
          'data-same-node': sameNodeAttr(row.nodeId, sameNodeId),
          'data-selected-node': selectedNodeAttr(row.nodeId, selectedId),
          'data-readiness': row.readinessLabels?.length > 0 ? 'true' : undefined,
          draggable: dragValue ? true : undefined,
        },
      },
    });
  };

  return (
    <div
      ref={containerRef}
      data-testid="condition-rail-tree"
      data-author-mode={authorMode ? 'true' : undefined}
      className="relative flex flex-col gap-px"
    >
      <svg
        data-testid="tree-connectors"
        className="pointer-events-none absolute inset-0 h-full w-full overflow-visible"
        aria-hidden="true"
      >
        {paths.map((p) => (
          <path key={p.key} d={p.d} fill="none" data-kind={p.kind} data-blocking={p.blocking ? 'true' : undefined} {...p.style} />
        ))}
      </svg>
      {authorMode && visible.map(renderAuthorRow)}
      {!authorMode && renderTrigger()}
      {!authorMode && !triggerCollapsed && !hasChildren && !triggerRow?.conditionText && (
        <div
          data-testid="rail-row"
          data-depth={1}
          data-rail-state="wait"
          data-gate-state="neutral"
          className="relative z-10"
          style={{ paddingLeft: INDENT_PX }}
        >
          <div className="flex items-center gap-1.5 min-w-0 rounded px-1.5 py-[3px]">
            <span className="h-[7px] w-[7px] flex-shrink-0 rounded-full border border-transparent bg-bg-3" aria-hidden="true" />
            <span className="min-w-0 truncate text-[11px] font-medium text-fg-2">No gating condition</span>
            <span className="flex-1" />
            <Chip text="—" state="neutral" invalid={false} />
          </div>
        </div>
      )}
      {!authorMode && visible.slice(1).map(renderRow)}
    </div>
  );
}
